
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    legacyApi,
    DocumentAttachment,
    Invoice,
    WorkflowEntityDefinition,
    WorkflowEventDefinition,
    WorkflowHistoryEntry
} from '../../../services/legacyApi';
import { Button } from '../../../components/ui/button';
import { CompactTable } from '../../../components/ui/CompactTable';
import { AlertTriangle, ArrowLeft, Download, Trash2, ExternalLink, Link2, MessageCircle, Send, Wallet } from 'lucide-react';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { TransitionActionBar } from '../components/TransitionActionBar';
import { StatusGuidePanel } from '../components/StatusGuidePanel';
import { StatusHistoryPanel } from '../components/StatusHistoryPanel';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { useAuth } from '../../../context/AuthContext';
import { DocumentAttachmentsCard } from '../components/DocumentAttachmentsCard';
import { DocumentPreviewDialog } from '../components/DocumentPreviewDialog';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { openWhatsAppMessage } from '../utils/whatsapp';

export const LegacyInvoiceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { user, hasPermission } = useAuth();
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [loading, setLoading] = useState(true);
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);
    const [transitionBusy, setTransitionBusy] = useState<string | null>(null);
    const [converting, setConverting] = useState(false);
    const [lastTransitionSummary, setLastTransitionSummary] = useState<{
        event?: string;
        old_status_name?: string;
        new_status_name?: string;
        branch_effects_applied?: string[];
    } | null>(null);
    const [ackInvoiceDocuments, setAckInvoiceDocuments] = useState<DocumentAttachment[]>([]);
    const [ackDnDocuments, setAckDnDocuments] = useState<DocumentAttachment[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [previewDocument, setPreviewDocument] = useState<DocumentAttachment | null>(null);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

    const hasLinkedDnForAcknowledgement = (invoiceData: Invoice | null) => {
        if (!invoiceData) return false;
        const primaryDnId = Number(invoiceData.dn_id || 0);
        const linkedDns = Array.isArray(invoiceData.linked_dns) ? invoiceData.linked_dns : [];
        return primaryDnId > 0 || linkedDns.some((dn) => Number(dn?.dn_id || 0) > 0);
    };

    const loadInvoiceDocuments = async (invoiceData: Invoice, options?: { silent?: boolean }) => {
        if (!options?.silent) setDocumentsLoading(true);
        try {
            const hasDnLinked = hasLinkedDnForAcknowledgement(invoiceData);
            const [invoiceDocs, dnDocs] = await Promise.all([
                legacyApi.sales.listInvoiceDocuments(invoiceData.invoice_id, 'ACK_INVOICE').catch(() => [] as DocumentAttachment[]),
                hasDnLinked
                    ? legacyApi.sales.listInvoiceDocuments(invoiceData.invoice_id, 'ACK_DN').catch(() => [] as DocumentAttachment[])
                    : Promise.resolve([] as DocumentAttachment[]),
            ]);
            setAckInvoiceDocuments(Array.isArray(invoiceDocs) ? invoiceDocs : []);
            setAckDnDocuments(Array.isArray(dnDocs) ? dnDocs : []);
        } catch (err) {
            console.error(err);
            setAckInvoiceDocuments([]);
            setAckDnDocuments([]);
        } finally {
            if (!options?.silent) setDocumentsLoading(false);
        }
    };

    const refreshInvoiceContext = async (invoiceId: number) => {
        const [invoiceData, definition, actions, historyData] = await Promise.all([
            legacyApi.sales.getInvoice(invoiceId),
            legacyApi.workflow.getDefinition('INVOICE').catch(() => null),
            legacyApi.workflow.getNextActions('INVOICE', invoiceId).catch(() => ({ next_actions: [] })),
            legacyApi.workflow.getHistory('INVOICE', invoiceId).catch(() => ({ history: [] })),
        ]);
        setInvoice(invoiceData);
        setWorkflowDefinition(definition);
        setNextActions((actions as any).next_actions || []);
        setHistory((historyData as any).history || []);
        await loadInvoiceDocuments(invoiceData);
    };

    useEffect(() => {
        if (!id) return;
        const invoiceId = parseInt(id, 10);
        refreshInvoiceContext(invoiceId)
            .catch((err) => {
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
    const canUndoPost = isAdmin && hasPermission('finance.invoice.undo_post');
    const visibleNextActions = useMemo(
        () =>
            (nextActions || []).filter((action) => {
                const code = String(action?.event_code || '').trim().toLowerCase();
                if (code === 'undo_post') return canUndoPost;
                return true;
            }),
        [nextActions, canUndoPost],
    );

    const handleDownloadPDF = async () => {
        if (!invoice) return;
        try {
            await legacyApi.sales.downloadInvoicePDF(invoice.invoice_id);
        } catch (err) {
            console.error(err);
            toast.error('Failed to download PDF');
        }
    };

    const handleRecordReceipt = () => {
        if (!invoice) return;
        const query = new URLSearchParams();
        query.set('tab', 'ar');
        query.set('mode', 'INVOICE_SETTLEMENT');
        query.set('invoice_id', String(invoice.invoice_id));
        if (invoice.customer_id) query.set('customer_id', String(invoice.customer_id));
        const outstanding = Number(invoice.outstanding_amount ?? 0);
        if (outstanding > 0) query.set('amount', outstanding.toFixed(2));
        navigate(`/financials?${query.toString()}`);
    };

    const handleDelete = async () => {
        if (!invoice) return;
        const linkedCreditNotes = invoice.credit_notes || [];
        if (linkedCreditNotes.length > 0) {
            toast.error('Cannot delete invoice linked to credit note. Keep it for audit and use cancel workflow.');
            return;
        }
        const statusId = Number(invoice.status_id || 1);
        if (![1, 4].includes(statusId)) {
            toast.error('Only Draft or Cancelled invoices can be deleted.');
            return;
        }
        const confirmed = await confirm(`Delete ${formatInvoiceId(invoice.invoice_id)}?`, {
            description: 'This permanently deletes this Draft/Cancelled invoice. This cannot be undone.',
            confirmText: 'Delete Invoice',
            variant: 'destructive'
        });

        if (confirmed) {
            try {
                await legacyApi.sales.deleteInvoice(invoice.invoice_id);
                navigate('/legacy/invoices');
            } catch (err: any) {
                toast.error(err.message || 'Failed to delete invoice');
            }
        }
    };

    const handleTransition = async (eventCode: string) => {
        if (!invoice) return;
        setTransitionBusy(eventCode);
        try {
            const result = await legacyApi.sales.transitionInvoice(invoice.invoice_id, eventCode, 'invoice detail transition');
            setLastTransitionSummary({
                event: result?.event,
                old_status_name: result?.old_status_name,
                new_status_name: result?.new_status_name,
                branch_effects_applied: result?.branch_effects_applied || [],
            });
            await refreshInvoiceContext(invoice.invoice_id);
            toast.success(`Invoice status updated: ${result?.old_status_name || '-'} -> ${result?.new_status_name || '-'}`);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update invoice status');
        } finally {
            setTransitionBusy(null);
        }
    };

    const handleConvertToTax = async () => {
        if (!invoice) return;
        setConverting(true);
        try {
            await legacyApi.sales.convertInvoiceToTax(invoice.invoice_id);
            await refreshInvoiceContext(invoice.invoice_id);
            toast.success('Proforma converted to Tax Invoice');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to convert invoice');
        } finally {
            setConverting(false);
        }
    };

    const resolveInvoiceRecipientEmail = async () => {
        if (!invoice?.customer_id) return '';
        const customers = await legacyApi.customers.list();
        return customers.find((customer) => Number(customer.customer_id) === Number(invoice.customer_id))?.email_address || '';
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!invoice) return;
        if (isDraftTaxInvoice(invoice)) {
            toast.error('Post this tax invoice before sending it to the customer.');
            return;
        }
        const result = await legacyApi.sales.emailInvoicePdf(invoice.invoice_id, payload);
        toast.success(result.message || 'Invoice PDF emailed successfully');
    };

    const handleUploadAcknowledgementDocument = async (
        category: 'ACK_INVOICE' | 'ACK_DN',
        payload: { file: File; note?: string; markCurrent: boolean },
    ) => {
        if (!invoice) return;
        try {
            await legacyApi.sales.uploadInvoiceDocument(invoice.invoice_id, {
                file: payload.file,
                category,
                note: payload.note,
                mark_current: payload.markCurrent,
            });
            toast.success(category === 'ACK_INVOICE' ? 'Acknowledged invoice uploaded' : 'Acknowledged DN uploaded');
            await refreshInvoiceContext(invoice.invoice_id);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload document');
            throw error;
        }
    };

    const handleSetCurrentAcknowledgementDocument = async (docId: number) => {
        if (!invoice) return;
        try {
            await legacyApi.sales.setInvoiceDocumentCurrent(invoice.invoice_id, docId);
            toast.success('Current document updated');
            await loadInvoiceDocuments(invoice, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update current document');
            throw error;
        }
    };

    const handleDownloadAcknowledgementDocument = async (docId: number) => {
        if (!invoice) return;
        try {
            await legacyApi.sales.downloadInvoiceDocument(invoice.invoice_id, docId);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to download document');
            throw error;
        }
    };

    const handlePreviewAcknowledgementDocument = (doc: DocumentAttachment) => {
        setPreviewDocument(doc);
    };

    const handleDeleteAcknowledgementDocument = async (docId: number) => {
        if (!invoice) return;
        const allowed = await confirm('Delete this document?', {
            description: 'The document will be archived (soft deleted) and removed from the active list.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!allowed) return;
        try {
            await legacyApi.sales.deleteInvoiceDocument(invoice.invoice_id, docId);
            toast.success('Document deleted');
            await loadInvoiceDocuments(invoice, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to delete document');
            throw error;
        }
    };

    const formatAmount = (val: number) => val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatInvoiceId = (id: number) => `HC/INV/${String(id).padStart(4, '0')}`;
    const isDraftTaxInvoice = (entry: Pick<Invoice, 'invoice_type' | 'status_id'>) =>
        (entry.invoice_type === 'PROFORMA' ? 'PROFORMA' : 'TAX') === 'TAX' && Number(entry.status_id || 1) === 1;

    const handleShareLink = async () => {
        if (!invoice) return;
        if (isDraftTaxInvoice(invoice)) {
            toast.error('Post this tax invoice before creating a customer share link.');
            return;
        }
        try {
            const result = await legacyApi.sales.createInvoiceShareLink(invoice.invoice_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Invoice share link created and copied' : 'Invoice share link copied');
            } catch {
                window.prompt('Copy invoice share link', result.share_url);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create invoice share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!invoice) return;
        if (isDraftTaxInvoice(invoice)) {
            toast.error('Post this tax invoice before sharing it on WhatsApp.');
            return;
        }
        try {
            const result = await legacyApi.sales.createInvoiceShareLink(invoice.invoice_id, { channel: 'WHATSAPP' });
            const message = `Please find invoice ${formatInvoiceId(invoice.invoice_id)}: ${result.share_url}`;
            const customers = await legacyApi.customers.list();
            const customer = customers.find((entry) => Number(entry.customer_id) === Number(invoice.customer_id));
            const resolved = openWhatsAppMessage(message, customer);
            if (!resolved.normalizedNumber) {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open WhatsApp share');
        }
    };

    const columns = [
        { header: "Description", accessor: (item: any) => item.part_name || `Item #${item.estimate_part_id}`, className: 'font-medium text-gray-900' },
        { header: "Qty", accessor: (item: any) => item.quantity, className: 'text-right font-mono' },
        { header: "Unit Price", accessor: (item: any) => formatAmount(item.unit_price), className: 'text-right font-mono' },
        { header: "Total", accessor: (item: any) => formatAmount(item.quantity * item.unit_price), className: 'text-right font-mono font-medium' },
    ];
    const filteredDetails = useMemo(() => {
        const details = invoice?.details || [];
        const query = state.q.trim().toLowerCase();
        if (!query) return details;
        return details.filter((item: any) =>
            String(item.part_name || '').toLowerCase().includes(query) ||
            String(item.estimate_part_id || '').toLowerCase().includes(query)
        );
    }, [invoice?.details, state.q]);

    if (loading) return <div className="p-8">Loading Invoice Details...</div>;
    if (!invoice) return <div className="p-8">Invoice not found.</div>;

    const statusId = Number(invoice.status_id || 1);
    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === statusId);
    const invoiceType = invoice.invoice_type === 'PROFORMA' ? 'PROFORMA' : 'TAX';
    const isDraft = Number(invoice.status_id || 1) === 1;
    const isDraftTax = invoiceType === 'TAX' && isDraft;
    const paidAmount = Number(invoice.paid_amount || 0);
    const grandTotal = Number(invoice.grand_total || 0);
    const estimateRef = invoice.estimate_ref || (invoice.estimate_id && invoice.estimate_id > 0 ? `HC/EST/${String(invoice.estimate_id).padStart(4, '0')}` : '');
    const isCreditInvoice = grandTotal < 0;
    const outstandingAmount = isCreditInvoice ? 0 : Number(invoice.outstanding_amount ?? invoice.grand_total ?? 0);
    const paymentProgress = isCreditInvoice
        ? 'Credit Note Posted'
        : (
            invoice.payment_progress ||
            (outstandingAmount <= 0 ? 'Fully Paid' : paidAmount > 0 ? 'Partially Paid' : 'Not Paid')
        );
    const relatedCreditNotes = invoice.credit_notes || [];
    const linkedDeliveryNotes = (invoice.linked_dns || []).filter((dn) => Number(dn?.dn_id || 0) > 0);
    const canDeleteInvoice = relatedCreditNotes.length === 0 && [1, 4].includes(statusId);
    const deleteDisabledReason = relatedCreditNotes.length > 0
        ? 'Cannot delete invoice linked to credit note.'
        : 'Only Draft or Cancelled invoices can be deleted.';
    const canViewInvoiceDocuments = hasPermission('finance.invoice.document.view');
    const canUploadInvoiceDocuments = hasPermission('finance.invoice.document.upload');
    const canDownloadInvoiceDocuments = hasPermission('finance.invoice.document.download');
    const canDeleteInvoiceDocuments = hasPermission('finance.invoice.document.delete');
    const hasDnLinksForAck = hasLinkedDnForAcknowledgement(invoice);

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
            <DocumentPreviewDialog
                open={Boolean(previewDocument)}
                document={previewDocument}
                previewUrl={previewDocument && invoice ? legacyApi.sales.previewInvoiceDocumentUrl(invoice.invoice_id, previewDocument.doc_id) : null}
                onOpenChange={(open) => !open && setPreviewDocument(null)}
                onDownload={(doc) => handleDownloadAcknowledgementDocument(doc.doc_id)}
            />
            <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0 flex items-center justify-between shadow-sm">
                <SendPdfModal
                    open={showSendPdfModal && !isDraftTax}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Invoice"
                    documentNumber={formatInvoiceId(invoice.invoice_id)}
                    attachmentName={`${formatInvoiceId(invoice.invoice_id).replace(/\//g, '_')}.pdf`}
                    defaultSubject={`Invoice ${formatInvoiceId(invoice.invoice_id)}`}
                    defaultBody={`Dear Customer,\n\nPlease find attached invoice ${formatInvoiceId(invoice.invoice_id)}.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPdf}
                    resolveDefaultTo={resolveInvoiceRecipientEmail}
                />
                <Button variant="ghost" onClick={() => navigate('/legacy/invoices')}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Invoices
                </Button>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleRecordReceipt}
                        disabled={!invoice.customer_id || outstandingAmount <= 0 || isCreditInvoice}
                        title={
                            !invoice.customer_id
                                ? 'Customer link is missing on this invoice'
                                : (isCreditInvoice ? 'Credit-note invoices are not settled via receipt posting' : undefined)
                        }
                    >
                        <Wallet className="w-4 h-4 mr-2" /> Record Receipt
                    </Button>
                    {invoiceType === 'PROFORMA' && isDraft && (
                        <Button variant="outline" onClick={handleConvertToTax} disabled={converting}>
                            {converting ? 'Converting...' : 'Convert to Tax Invoice'}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={handleDelete}
                        disabled={!canDeleteInvoice}
                        title={canDeleteInvoice ? undefined : deleteDisabledReason}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:text-slate-400 disabled:hover:bg-transparent"
                    >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                    <Button onClick={handleDownloadPDF}>
                        <Download className="w-4 h-4 mr-2" /> {isDraftTax ? 'Download Draft Preview' : 'Download PDF'}
                    </Button>
                    {hasPermission('finance.invoice.email') && (
                        <Button
                            variant="outline"
                            onClick={() => setShowSendPdfModal(true)}
                            disabled={isDraftTax}
                            title={isDraftTax ? 'Post before sending to customer as a final invoice.' : undefined}
                        >
                            <Send className="w-4 h-4 mr-2" /> Send
                        </Button>
                    )}
                    {hasPermission('finance.invoice.share') && (
                        <>
                            <Button
                                variant="outline"
                                onClick={handleShareLink}
                                disabled={isDraftTax}
                                title={isDraftTax ? 'Post before creating a customer share link.' : undefined}
                            >
                                <Link2 className="w-4 h-4 mr-2" /> Share Link
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleShareWhatsApp}
                                disabled={isDraftTax}
                                title={isDraftTax ? 'Post before sharing on WhatsApp.' : undefined}
                            >
                                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-6">
                {isDraftTax && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                            <div>
                                <div className="font-semibold">Draft invoice is not included in Customer SOA until posted.</div>
                                <div>Post before sending to customer as a final invoice. Download remains available as a draft preview only.</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    {/* Header */}
                    <div className="bg-purple-50 p-6 border-b border-purple-100 flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-1">{`Invoice ${formatInvoiceId(invoice.invoice_id)}`}</h1>
                            <p className="text-sm text-purple-700">Date: {new Date(invoice.invoice_date!).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-500 mb-1">Total Amount</div>
                            <div className="text-3xl font-bold text-purple-600">AED {formatAmount(invoice.grand_total || 0)}</div>
                            <div className="mt-2 flex justify-end gap-1">
                                <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                                    invoiceType === 'PROFORMA'
                                        ? 'border-amber-200 bg-amber-100 text-amber-700'
                                        : 'border-emerald-200 bg-emerald-100 text-emerald-700'
                                }`}>
                                    {invoiceType}
                                </span>
                                <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                    {invoice.status_name || 'Draft'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Meta Data */}
                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 border-b">
                        <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Customer</h3>
                            <p className="font-medium text-gray-900">{invoice.customer_name}</p>
                            <p className="text-sm text-gray-600 truncate" title={invoice.customer_address || ''}>{invoice.customer_address}</p>
                            <p className="text-sm text-gray-600">Emirate: {invoice.customer_emirate || '-'}</p>
                            <p className="text-sm text-gray-600">TRN: {invoice.customer_trn || '-'}</p>
                        </div>
                        <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">References</h3>
                            <div className="space-y-1">
                                <p className="text-sm flex items-center">
                                    <span className="text-gray-500 w-24">Estimate:</span>
                                    {invoice.estimate_id && invoice.estimate_id > 0 ? (
                                        <Link to={`/legacy/estimates/${invoice.estimate_id}`} className="text-blue-600 hover:underline flex items-center">
                                            {estimateRef}
                                            <ExternalLink className="w-3 h-3 ml-1" />
                                        </Link>
                                    ) : (
                                        <span className="font-medium text-slate-700">Direct SO</span>
                                    )}
                                </p>
                                {invoice.so_id && (
                                    <p className="text-sm flex items-center">
                                        <span className="text-gray-500 w-24">Sale Order:</span>
                                        <Link to={`/legacy/orders/${invoice.so_id}`} className="text-blue-600 hover:underline flex items-center">
                                            HC/SO/{String(invoice.so_id).padStart(4, '0')}
                                            <ExternalLink className="w-3 h-3 ml-1" />
                                        </Link>
                                    </p>
                                )}
                                {invoice.wo_id && (
                                    <p className="text-sm flex items-center">
                                        <span className="text-gray-500 w-24">Job Card:</span>
                                        <span className="font-mono text-slate-700">{invoice.wo_number || `WO-${invoice.wo_id}`}</span>
                                    </p>
                                )}
                                {invoice.source_summary && (
                                    <p className="text-sm flex items-center">
                                        <span className="text-gray-500 w-24">Source:</span>
                                        <span className="text-slate-700">{invoice.source_summary}</span>
                                    </p>
                                )}
                                <div className="text-sm">
                                    <span className="text-gray-500 inline-block w-24 align-top">Linked DNs:</span>
                                    {linkedDeliveryNotes.length === 0 ? (
                                        <span className="text-slate-700">-</span>
                                    ) : (
                                        <span className="inline-flex flex-wrap gap-2">
                                            {linkedDeliveryNotes.map((dn) => (
                                                <Link
                                                    key={dn.dn_id}
                                                    to={`/legacy/delivery/${dn.dn_id}`}
                                                    className="inline-flex items-center rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-700 hover:bg-slate-200"
                                                >
                                                    {dn.dn_number || `HC/DN/${String(dn.dn_id).padStart(4, '0')}`}
                                                </Link>
                                            ))}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm flex items-center">
                                    <span className="text-gray-500 w-24">Customer PO:</span>
                                    <span className="font-mono">{invoice.customer_po_no || '-'}</span>
                                </p>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Lifecycle Actions</h3>
                            <TransitionActionBar
                                actions={visibleNextActions}
                                onAction={handleTransition}
                                busyEventCode={transitionBusy}
                                disabled={converting}
                                precheckEntity="INVOICE"
                                precheckEntityId={invoice.invoice_id}
                            />
                        </div>
                    </div>

                    <div className="space-y-4 border-b bg-white p-4">
                        <div className="space-y-4">
                            <div className="rounded-md border border-slate-200 bg-white p-3">
                                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Status Guide</p>
                                <StatusGuidePanel
                                    statusLabel={invoice.status_name || 'Draft'}
                                    statusMeaning={statusDefinition?.status_meaning}
                                    nextActions={visibleNextActions}
                                />
                            </div>
                        </div>
                        <WorkflowRightSidebar mode="overlay" title="Invoice Workflow & Status">
                            <WorkflowRightPanel
                                title="Workflow & Status"
                                description="Next actions, status, and prechecks"
                            >
                                <FlowCoachPanel entity="INVOICE" entityId={invoice.invoice_id} />
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Status History</p>
                                    <StatusHistoryPanel history={history} />
                                </div>
                            </WorkflowRightPanel>
                        </WorkflowRightSidebar>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-b bg-slate-50 p-4">
                        <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Finance Impact</p>
                            {Number(invoice.status_id || 1) === 1 && <p>Draft only. No AR posting until invoice is posted.</p>}
                            {Number(invoice.status_id || 1) === 2 && <p>Posted. AR is recognized and awaiting receipt reconciliation.</p>}
                            {Number(invoice.status_id || 1) === 3 && <p>Paid. AR is settled and invoice lifecycle is complete.</p>}
                            {Number(invoice.status_id || 1) === 4 && <p>Cancelled. Review reversal impact in audit trail.</p>}
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Settlement Snapshot</p>
                            <div className="space-y-1 text-xs">
                                <p><span className="font-medium">Paid:</span> AED {formatAmount(paidAmount)}</p>
                                <p><span className="font-medium">Outstanding:</span> AED {formatAmount(outstandingAmount)}</p>
                                <p><span className="font-medium">Progress:</span> {paymentProgress}</p>
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Last Transition Summary</p>
                            {lastTransitionSummary ? (
                                <div className="space-y-1 text-xs">
                                    <p><span className="font-medium">Event:</span> {lastTransitionSummary.event || '-'}</p>
                                    <p><span className="font-medium">Status:</span> {lastTransitionSummary.old_status_name || '-'} {'->'} {lastTransitionSummary.new_status_name || '-'}</p>
                                    <p><span className="font-medium">Branch effects:</span> {(lastTransitionSummary.branch_effects_applied || []).join(', ') || 'none'}</p>
                                </div>
                            ) : (
                                <p className="text-xs text-muted-foreground">No transition executed in this session.</p>
                            )}
                        </div>
                    </div>

                    <div className="border-b bg-white p-4">
                        <div className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Related Credit Notes</p>
                                <Button variant="outline" size="sm" onClick={() => navigate('/legacy/credit-notes')}>
                                    View All Credit Notes
                                </Button>
                            </div>
                            {relatedCreditNotes.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No credit notes linked to this invoice.</p>
                            ) : (
                                <div className="space-y-2">
                                    {relatedCreditNotes.map((cn) => (
                                        <div key={cn.cn_id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                                            <div className="flex items-center gap-3">
                                                <Link to={`/legacy/credit-notes/${cn.cn_id}`} className="font-mono text-blue-600 hover:underline">
                                                    {cn.cn_number}
                                                </Link>
                                                <span className="text-slate-500">{cn.cn_date ? new Date(cn.cn_date).toLocaleDateString() : '-'}</span>
                                                <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                                    {cn.status_name}
                                                </span>
                                                <span className="font-medium text-slate-700">AED {formatAmount(cn.grand_total || 0)}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="outline" size="sm" onClick={() => navigate(`/legacy/credit-notes/${cn.cn_id}`)}>
                                                    View
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => legacyApi.sales.downloadCreditNotePDF(cn.cn_id)}>
                                                    Print
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {canViewInvoiceDocuments && (
                        <div className="space-y-4 border-b bg-white p-4">
                            <DocumentAttachmentsCard
                                title="Acknowledged Invoice"
                                emptyLabel="No acknowledged invoice files uploaded."
                                documents={ackInvoiceDocuments}
                                loading={documentsLoading}
                                canUpload={canUploadInvoiceDocuments}
                                canDownload={canDownloadInvoiceDocuments}
                                canDelete={canDeleteInvoiceDocuments}
                                onUpload={(payload) => handleUploadAcknowledgementDocument('ACK_INVOICE', payload)}
                                onSetCurrent={(docId) => handleSetCurrentAcknowledgementDocument(docId)}
                                onPreview={handlePreviewAcknowledgementDocument}
                                onDownload={handleDownloadAcknowledgementDocument}
                                onDelete={handleDeleteAcknowledgementDocument}
                                onRefresh={() => refreshInvoiceContext(invoice.invoice_id)}
                            />
                            <DocumentAttachmentsCard
                                title="Acknowledged Delivery Notes"
                                emptyLabel={
                                    hasDnLinksForAck
                                        ? 'No acknowledged delivery-note files uploaded.'
                                        : 'No linked delivery notes on this invoice yet.'
                                }
                                documents={ackDnDocuments}
                                loading={documentsLoading}
                                canUpload={canUploadInvoiceDocuments && hasDnLinksForAck}
                                canDownload={canDownloadInvoiceDocuments}
                                canDelete={canDeleteInvoiceDocuments}
                                onUpload={(payload) => handleUploadAcknowledgementDocument('ACK_DN', payload)}
                                onSetCurrent={(docId) => handleSetCurrentAcknowledgementDocument(docId)}
                                onPreview={handlePreviewAcknowledgementDocument}
                                onDownload={handleDownloadAcknowledgementDocument}
                                onDelete={handleDeleteAcknowledgementDocument}
                                onRefresh={() => refreshInvoiceContext(invoice.invoice_id)}
                            />
                            <p className="text-xs text-slate-500">
                                Upload signed DN copies; include DN number in note for quick reference.
                            </p>
                        </div>
                    )}

                    {/* Line Items */}
                    <div className="p-0">
                        <div className="p-4">
                            <TableFilterBar
                                searchValue={state.q}
                                onSearchChange={(value) => setFilter('q', value)}
                                searchPlaceholder="Search line items..."
                                isDirty={isDirty}
                                onClear={clearAll}
                                rightSlot={`${filteredDetails.length} results`}
                            />
                        </div>
                        <CompactTable data={filteredDetails} columns={columns} keyField="invoice_detail_id" />

                        {/* Footer with calculations */}
                        <div className="bg-gray-50 border-t mt-4">
                            <div className="px-6 py-2 flex justify-between items-center border-b border-gray-200">
                                <span className="text-gray-500">Subtotal</span>
                                <span className="text-gray-900 font-medium">{formatAmount(invoice.sub_total || 0)}</span>
                            </div>
                            <div className="px-6 py-2 flex justify-between items-center border-b border-gray-200">
                                <span className="text-gray-500">Discount ({(invoice.discount_percent ?? 0).toFixed(2)}%)</span>
                                <span className="text-gray-900 font-medium">{formatAmount(invoice.discount_amount || 0)}</span>
                            </div>
                            <div className="px-6 py-2 flex justify-between items-center border-b border-gray-200">
                                <span className="text-gray-500">VAT ({invoice.vat_percent}%)</span>
                                <span className="text-gray-900 font-medium">{formatAmount(invoice.vat_amount || 0)}</span>
                            </div>
                            <div className="px-6 py-3 flex justify-between items-center border-t border-gray-200">
                                <span className="font-bold text-gray-900">Grand Total</span>
                                <span className="font-bold text-purple-600">{formatAmount(invoice.grand_total || 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
